import React from 'react'
import { createRoot } from 'react-dom/client'
import { App } from './App'
import { captureUrlLoginToken } from './providers/bootstrap'
import { getFonts, getStickyOffset, getTheme } from './providers/embedConfig'
import { unclipStickyAncestors } from './util/stickyAncestors'
// Imported as a raw string (not injected into <head>) so we can attach it to the shadow
// root instead. This is the real styling path from the design spec: CSS authored normally,
// delivered into the shadow root via a constructable stylesheet.
import baseCss from './styles/base.css?inline'

/**
 * <tradein-app> — the custom element a storefront embeds.
 *
 * Instead of v1's light-DOM `#tradeinroot` mount, v2 renders React into a Shadow Root so the
 * host storefront's CSS cannot cascade in and our CSS cannot leak out. All styles live on the
 * shadow root via `adoptedStyleSheets` — nothing lands in the host <head>.
 *
 * Shadow-root layout:
 *   #shadow-root
 *   ├─ <div> app mount        → <App>, whose `.app` wrapper sets container-type
 *   └─ <div> portal container → full-screen overlays + toast (see providers/portal.tsx)
 * The portal container is a SIBLING of the app mount so `position: fixed` overlays resolve
 * against the viewport, not the container-query containing block on `.app`.
 */

const sheet = new CSSStyleSheet()
sheet.replaceSync(baseCss)

/**
 * Which colour theme to skin the widget with. Configured by the storefront either as a `theme`
 * attribute on the element (`<tradein-app theme="blue">`) or via `tradeInConfig.theme`. The value maps
 * 1:1 to a `:host([data-theme="..."])` palette in base.css ("blue", "grey", "squaregrey", "ink",
 * "squareink", "wine", "squarewine", "stone", "linen", "squarelinen", "teal", "slate", "rose",
 * "dark", "spejdersport"); anything unrecognised falls through to the default light palette. The
 * attribute wins over the config. The value may list a base theme followed by brand layers
 * ("squaregrey gailisva"): base.css selects palettes as words (`~=`), so each layer applies on top.
 *
 * A brand-named theme (spejdersport) may also set `--sans`/`--serif` to the storefront's own webfonts.
 * Fonts are the one thing the shadow root does NOT isolate: `@font-face` resolves from the HOST
 * document, so inside the storefront the brand's already-loaded faces just work — while a standalone
 * page (tradein2.create2stay.com) or `vite` dev must load them itself in index.html or fall back down
 * the stack. Spejder Sport's two faces are Geist (body, self-hosted on their Shopify CDN) and Alverata
 * Informal (headings, Adobe Fonts kit https://use.typekit.net/xal1xje.css) — and an Adobe kit only
 * serves its allow-listed domains, so a standalone deploy needs that domain added to the kit.
 */
function readTheme(el: HTMLElement): string | null {
  return el.getAttribute('theme') || getTheme()
}

/**
 * Optional body and heading font families. Custom faces must already be loaded by the host document.
 */
function applyFonts(el: HTMLElement): void {
  const fonts = getFonts()
  const body = fonts?.body?.trim()
  const heading = fonts?.heading?.trim()
  if (body) el.style.setProperty('--sans', body)
  if (heading) {
    el.style.setProperty('--heading', heading)
    // Value figures and redemption codes use the existing display-face token.
    el.style.setProperty('--serif', heading)
  }
}

/**
 * How far below the viewport top the sticky topbar should stick — so it lands *below* a host
 * storefront's own sticky header instead of hiding behind it. Configured like the theme: a
 * `sticky-offset` attribute or `tradeInConfig.stickyOffset`. A bare number is treated as px;
 * a full CSS length (e.g. "5rem", or a `calc()` over the host theme's own header-height vars)
 * is used verbatim. Feeds the `--ti-sticky-offset` CSS var.
 */
function readStickyOffset(el: HTMLElement): string | null {
  const attr = el.getAttribute('sticky-offset')
  const cfg = getStickyOffset()
  const raw = attr ?? (cfg != null ? String(cfg) : null)
  if (raw == null || raw.trim() === '') return null
  const v = raw.trim()
  return /^-?\d+(\.\d+)?$/.test(v) ? `${v}px` : v
}

/**
 * Types `--ti-sticky-offset` as a `<length>`, so an offset built from the host theme's vars
 * (`calc(var(--header-height) + …)`) computes to plain px. Untyped, it would read back as the
 * literal `calc(…)` text, which `util/scroll` cannot parse. Registration is document-global and
 * can only happen once, hence the guard against a second copy of the bundle on the page.
 */
function registerStickyOffsetProperty(): void {
  if (typeof CSS === 'undefined' || !('registerProperty' in CSS)) return
  try {
    CSS.registerProperty({ name: '--ti-sticky-offset', syntax: '<length>', inherits: true, initialValue: '0px' })
  } catch {
    // Already registered by another copy of the bundle — same definition, nothing to do.
  }
}

registerStickyOffsetProperty()

class TradeInApp extends HTMLElement {
  /** Undoes our host-page overflow repair; see `unclipStickyAncestors`. */
  private restoreAncestorOverflow: (() => void) | null = null

  connectedCallback() {
    // Guard against re-entry if the element is moved/re-connected in the DOM.
    if (this.shadowRoot) return

    // Capture a magic-link token from the URL (?login= or a /apps/retrade/<uuid> path) into the
    // store and strip it from the address bar — before React first reads bootstrap. (v1 parity.)
    captureUrlLoginToken()

    // Apply the configured theme to the host so `:host([data-theme="..."])` tokens take effect.
    const theme = readTheme(this)
    if (theme) this.setAttribute('data-theme', theme)

    applyFonts(this)

    // Push the sticky topbar below the host's own sticky header, if the shop configured an offset.
    const stickyOffset = readStickyOffset(this)
    if (stickyOffset) this.style.setProperty('--ti-sticky-offset', stickyOffset)

    // …and make sure `position: sticky` can reach the viewport at all: a theme wrapper at
    // `overflow: hidden` would otherwise capture it and the topbar would never pin.
    this.restoreAncestorOverflow = unclipStickyAncestors(this)

    const shadow = this.attachShadow({ mode: 'open' })
    shadow.adoptedStyleSheets = [sheet]

    const mount = document.createElement('div')
    shadow.appendChild(mount)

    const portalContainer = document.createElement('div')
    portalContainer.className = 'ti-portal-root'
    shadow.appendChild(portalContainer)

    createRoot(mount).render(
      <React.StrictMode>
        <App portalContainer={portalContainer} />
      </React.StrictMode>,
    )
  }

  /** Hand the storefront's own wrapper styles back if the embed is ever removed from the page. */
  disconnectedCallback() {
    this.restoreAncestorOverflow?.()
    this.restoreAncestorOverflow = null
  }
}

if (!customElements.get('tradein-app')) {
  customElements.define('tradein-app', TradeInApp)
}
